import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { StepProps, useFlowState, useMatchMedia } from '@betterment/js-runtime';
import { Button, Icon } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import {
  Box,
  CenteredWorkflowLayout,
  Flex,
  HR,
  Heading,
  Label,
  Loader,
  Text,
  WorkflowHeader,
} from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import { Form, Formik } from 'formik';
import _isEmpty from 'lodash/isEmpty';
import _startCase from 'lodash/startCase';
import _toLower from 'lodash/toLower';

import PortfolioCustomizationFooter from './PortfolioCustomizationFooter';
import PortfolioTickerDetailsModal from './PortfolioTickerDetailsModal';

import { trackClick } from '../../features/track';
import Bullet from '../../Shared/components/Bullet';
import List from '../../Shared/components/List';
import { FormikNumberInput } from '../../Shared/components/NumberInput';
import { locationAssign } from '../../Shared/path-helpers';
import { urlBuilder } from '../../Shared/urlBuilder';
import {
  Allocations,
  CollapsedSectionsState,
  FundListProps,
  FundReallocation,
  PortfolioFlowState,
  WeightedSecuritiesWithAllocations,
} from '../data';
import { useWeightedSecuritiesForGoalQuery } from '../hooks';
import {
  convertToPercentDisplay,
  currentPortfolioName,
  currentPortfolioSubName,
  newPortfolioName,
  newPortfolioSubname,
  portfolioAllocationsChanged,
  totalAllocationWeight,
} from '../models';

const Header = styled(Heading)`
  white-space: nowrap;
`;

const CollapseSecuritiesWrapper = styled(Box)`
  && {
    cursor: pointer;
  }
`;

const FundList: React.FC<FundListProps> = ({
  group,
  setSelectedSecurity,
  setHasPortfolioTickerDetailsModalDisplayed,
  toggleSectionCollapsed,
  collapsedSections,
  isMobileVersion,
}) => {
  const { grandparentGroupName, securities } = group;
  return (
    <React.Fragment key={grandparentGroupName}>
      <Flex mt="10" mb="2" justifyContent="space-between" alignItems="end">
        <Box>
          <Text as="h3">{_startCase(_toLower(grandparentGroupName))}</Text>
        </Box>
        <CollapseSecuritiesWrapper
          aria-label={`toggle-collapse-${grandparentGroupName}-section`}
          onClick={() => toggleSectionCollapsed(`is${grandparentGroupName}Collapsed`)}
        >
          {!collapsedSections?.[`is${grandparentGroupName}Collapsed`] ? (
            <Icon name="CaretUp" size="small" />
          ) : (
            <Icon name="CaretDown" size="small" />
          )}
        </CollapseSecuritiesWrapper>
      </Flex>
      <HR />

      {!collapsedSections?.[`is${grandparentGroupName}Collapsed`] &&
        securities.map(fund => {
          const { symbol, name, slug, historicalPerformances, expensePercent } = fund.security;

          return (
            <Box key={slug} pt="6">
              <Flex justifyContent="space-between" alignItems="center">
                <Flex.Item>
                  <Label mb="0" htmlFor={slug}>
                    {name}
                  </Label>
                  <Text as="p" fontSize="1" color="bmtGrey50" lineHeight="1">
                    {symbol}
                  </Text>
                </Flex.Item>
                <Flex.Item>
                  <FormikNumberInput
                    min="0"
                    max="100"
                    step="1"
                    style={{ borderRadius: '.3rem' }}
                    name={slug}
                    id={slug}
                    unit="percent"
                    placeholder="0"
                    mb="6"
                  />
                </Flex.Item>
              </Flex>
              <Box width={isMobileVersion ? '100%' : '60%'} mb="4">
                <Flex justifyContent="space-between" alignItems="center">
                  {historicalPerformances
                    .filter(item => item.timePeriod !== 'Inception')
                    .map(item => (
                      <Flex.Item key={item.timePeriod}>
                        <Text as="p" mb="0" fontSize="0" lineHeight="0">
                          {convertToPercentDisplay(item.percent)}
                        </Text>
                        <Text as="p" color="bmtGrey50" fontSize="0" lineHeight="0">
                          {item.timePeriod}
                        </Text>
                      </Flex.Item>
                    ))}
                  <Flex.Item>
                    <Text as="p" mb="0" fontSize="0" lineHeight="0">
                      {convertToPercentDisplay(expensePercent)}
                    </Text>
                    <Text as="p" color="bmtGrey50" fontSize="0" lineHeight="0">
                      Exp ratio %
                    </Text>
                  </Flex.Item>
                </Flex>
                <Box mt="3" mb="6">
                  <Button
                    variant="textPrimary"
                    onClick={() => {
                      trackClick({ name: `SeeMore${slug}`, flow: 'ExternallyAdvisedPlanFlow' });
                      setSelectedSecurity(fund.security);
                      setHasPortfolioTickerDetailsModalDisplayed(true);
                    }}
                  >
                    <Text color="bmtBlue" fontSize="1">
                      See more
                    </Text>
                  </Button>
                </Box>
              </Box>
              <HR />
            </Box>
          );
        })}
    </React.Fragment>
  );
};

const PortfolioCustomizationStep: React.FC<React.PropsWithChildren<StepProps>> = ({ goToStep }) => {
  const {
    flowState: {
      values: { goalId, fundReallocation },
    },
  } = useFlowState<PortfolioFlowState>();
  const { isLoading, weightedSecurities } = useWeightedSecuritiesForGoalQuery(goalId);
  const isMobileVersion = !useMatchMedia('atLeastMedium');

  const [hasPortfolioTickerDetailsModalDisplayed, setHasPortfolioTickerDetailsModalDisplayed] = useState(false);
  const [selectedSecurity, setSelectedSecurity] = useState<WeightedSecuritiesWithAllocations[number]['security']>();
  const [allocations, setAllocations] = useState<Allocations>({});

  const dataIsReady = () => {
    return isLoading === false && _isEmpty(allocations) === false;
  };

  useEffect(() => {
    if (weightedSecurities) {
      const currentAllocations = Object.fromEntries(
        weightedSecurities.map(fund => [fund.security.slug, fund.allocationPercent])
      );

      const allocationState = fundReallocation?.fundAllocations || currentAllocations;

      setAllocations(allocationState);
    }
  }, [weightedSecurities, fundReallocation]);

  const securitiesByGrandparentName = useMemo(() => {
    const fundGroupsMap: Map<string, WeightedSecuritiesWithAllocations> = new Map();

    weightedSecurities?.forEach(security => {
      const grandparentName = security.security.grandparentGroupName;
      if (grandparentName) {
        if (fundGroupsMap.has(grandparentName)) {
          fundGroupsMap.get(grandparentName)?.push(security);
        } else {
          fundGroupsMap.set(grandparentName, [security]);
        }
      }
    });

    return Array.from(fundGroupsMap, ([grandparentGroupName, securities]) => ({
      grandparentGroupName,
      securities,
    }));
  }, [weightedSecurities]);

  const initialCollapsedState: CollapsedSectionsState = useMemo(() => {
    const state: CollapsedSectionsState = {};
    securitiesByGrandparentName.forEach(({ grandparentGroupName }) => {
      state[`is${grandparentGroupName}Collapsed`] = false;
    });
    return state;
  }, [securitiesByGrandparentName]);

  const [collapsedSections, setCollapsedSections] = useState(initialCollapsedState);

  const toggleSectionCollapsed = (sectionName: keyof CollapsedSectionsState) => {
    setCollapsedSections(prevState => ({
      ...prevState,
      [sectionName]: !prevState[sectionName],
    }));
  };

  const handleSubmit = (values: Allocations) => {
    const currentPortfolioNameVar = currentPortfolioName(weightedSecurities!);

    if (portfolioAllocationsChanged(values, weightedSecurities!)) {
      const newFundReallocation: FundReallocation = {
        fromName: currentPortfolioNameVar,
        fromSubname: currentPortfolioSubName(weightedSecurities!),
        toName: newPortfolioName(values, weightedSecurities!),
        toSubname: newPortfolioSubname(values, currentPortfolioNameVar, weightedSecurities!),
        fundAllocations: values,
      };
      goToStep('confirm', { fundReallocation: newFundReallocation, previousStep: 'customize' });
    } else {
      goToStep('success');
    }
  };

  const validateAllocations = (values: Allocations) => {
    const errors: Record<string, string> = {};
    const sum: string | number = totalAllocationWeight(values);
    if (sum !== 100) {
      errors.form = 'Must be 100% to continue';
    }
    return errors;
  };

  const closePortfolioTickerDetailsModal = useCallback(() => {
    setHasPortfolioTickerDetailsModalDisplayed(false);
  }, []);

  return (
    <CenteredWorkflowLayout variant="six-column">
      <CenteredWorkflowLayout.Slot name="header">
        <WorkflowHeader
          title=""
          backButtonHandler={() => goToStep('select')}
          closeHandler={() => locationAssign(urlBuilder.getGoalSettings.absolute({ goalId }))}
        />
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="content">
        <Box mb="8">
          <Header variant="primary">Build your custom portfolio</Header>
          <Text as="p" mb="8">
            Your specified portfolio weights will set the target allocation for the overall portfolio. Your
            contributions may be used to reduce portfolio drift.
          </Text>
          <Text as="p">Things to know:</Text>
          <List>
            <Bullet>Past performance does not guarantee future results.</Bullet>
            <Bullet>
              Fees and expenses can substantially reduce your investment returns, and are one of several factors you
              should consider with investing.{' '}
              <SambaAutoLink
                onClick={() => trackClick({ name: 'Learn More', flow: 'ExternallyAdvisedPlanFlow' })}
                to={urlBuilder.getEBSALookAt401kPlanFeesUrl()}
                target="_blank"
              >
                Learn more
              </SambaAutoLink>
            </Bullet>
            <Bullet>
              Select the fund’s ticker hyperlink for linked prospectuses to view information on shareholder-type fees or
              visit{' '}
              <SambaAutoLink
                onClick={() => trackClick({ name: 'Google Finance', flow: 'ExternallyAdvisedPlanFlow' })}
                to={urlBuilder.getGoogleFinanceUrl()}
                target="_blank"
              >
                Google Finance
              </SambaAutoLink>{' '}
              for additional information on each fund
            </Bullet>
          </List>
        </Box>
        {!dataIsReady() && <Loader mt="10" isLoading={isLoading} />}
        {dataIsReady() && (
          <Formik
            onSubmit={handleSubmit}
            initialValues={allocations}
            validateOnBlur={true}
            validateOnMount={true}
            validate={validateAllocations}
          >
            {({ values, errors }) => (
              <Form style={{ position: 'relative' }}>
                <>
                  {securitiesByGrandparentName.map(group => (
                    <FundList
                      key={group.grandparentGroupName}
                      group={group}
                      setSelectedSecurity={setSelectedSecurity}
                      setHasPortfolioTickerDetailsModalDisplayed={setHasPortfolioTickerDetailsModalDisplayed}
                      toggleSectionCollapsed={toggleSectionCollapsed}
                      collapsedSections={collapsedSections}
                      isMobileVersion={isMobileVersion}
                    />
                  ))}

                  {hasPortfolioTickerDetailsModalDisplayed && (
                    <PortfolioTickerDetailsModal
                      security={selectedSecurity}
                      closeHandler={closePortfolioTickerDetailsModal}
                    />
                  )}
                  <PortfolioCustomizationFooter currentAllocations={values} errors={errors} />
                </>
              </Form>
            )}
          </Formik>
        )}
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default PortfolioCustomizationStep;
